import React from 'react';
import moment from 'moment';
import { Field, submit } from 'redux-form';

import { TextField as TextFieldUI } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import EventIcon from '@material-ui/icons/Event';
import { KeyboardDatePicker } from '@material-ui/pickers';

import { DATE_FORMAT, TIME_FORMAT } from 'constants/config';

import { DatePickerModal } from 'components/Form/DatePickerModal';

const FilterDateRangeField = (props) => {
  const { source, searchPath = '', ...rest } = props;
  return (
    <Field
      component={FieldComponent}
      name={source}
      submitOnChange={true}
      validate={(val) => {
        if (!val) return null;
        const { q } = val;
        if (!q) return null;
        const { $gte, $lte } = q;
        if ($gte && $lte && moment($gte).isSameOrAfter($lte)) {
          return { to: 'не может быть меньше даты, с' };
        }
        return '';
      }}
      format={(v) => (v ? v.q : null)}
      parse={(v) => {
        return v ? { q: v, qp: searchPath } : null;
      }}
      {...rest}
    />
  );
};

export default React.memo(FilterDateRangeField);

const FieldComponent = (props) => {
  const { input = {}, meta = {}, dateType = 'date', ...attrs } = props;

  const {
    disabled,
    helperText = [],
    labels = ['Дата, с', 'Дата, по'],
    submitOnChange,
    dateformat,
    clearable = true
  } = attrs;

  const [labelTextFrom, labelTextTo] = labels;
  const [helperTextFrom, helperTextTo] = helperText;
  const error = meta.error;
  const error_from = error && error.from;
  const error_to = error && error.to;

  let df = DATE_FORMAT;
  if (dateType === 'datetime') {
    df = `${DATE_FORMAT}, ${TIME_FORMAT}`;
  } else if (dateType === 'time') {
    df = `${TIME_FORMAT}`;
  }
  if (dateformat) df = dateformat;

  const dispatch = meta.dispatch;
  const form = meta.form;

  const parseValue = (value, type) => {
    let res = null;
    if (value) {
      if (dateType === 'time') {
        res = value.second(0).format('HH:mm:ss');
      } else if (dateType === 'datetime') {
        res = value.second(0).toISOString();
      } else {
        res =
          type === 'to'
            ? value.endOf('day').toISOString()
            : value.startOf('day').toISOString();
      }
    }
    return res;
  };

  const formatValue = (value) => {
    let res = null;
    if (value && dateType === 'time') {
      res = moment(value, [moment.ISO_8601, 'HH:mm:ss']);
    } else if (value) {
      res = moment(value);
    }
    return res;
  };

  const value = input.value || null;
  const from = formatValue((value && value.$gte) || null);
  const to = formatValue((value && value.$lte) || null);

  const [openFrom, setOpenFrom] = React.useState(false);
  const [openTo, setOpenTo] = React.useState(false);
  const [pickerErrorFrom, setPickerErrorFrom] = React.useState(null);
  const [pickerErrorTo, setPickerErrorTo] = React.useState(null);
  const [clearKeyFrom, setClearKeyFrom] = React.useState(0);
  const [clearKeyTo, setClearKeyTo] = React.useState(0);

  const _onChange = (fromVal, toVal) => {
    let res = null;
    if (fromVal || toVal) {
      res = {};
      if (fromVal) res.$gte = fromVal;
      if (toVal) res.$lte = toVal;
    }
    input.onChange(res);
    setTimeout(() => {
      if (submitOnChange) dispatch(submit(form));
    }, 0);
  };

  const _onChangeFrom = (date, textValue) => {
    if (date === null || !textValue) {
      setPickerErrorFrom(null);
      setClearKeyFrom((k) => k + 1);
      _onChange(null, parseValue(to, 'to'));
      return;
    }
    if (date.isValid()) {
      setPickerErrorFrom(null);
      _onChange(parseValue(date, 'from'), parseValue(to, 'to'));
    } else if (!textValue.includes('_')) {
      setPickerErrorFrom('Неверный формат даты');
    }
  };

  const _onChangeTo = (date, textValue) => {
    if (date === null || !textValue) {
      setPickerErrorTo(null);
      setClearKeyTo((k) => k + 1);
      _onChange(parseValue(from, 'from'), null);
      return;
    }
    if (date.isValid()) {
      setPickerErrorTo(null);
      _onChange(parseValue(from, 'from'), parseValue(date, 'to'));
    } else if (!textValue.includes('_')) {
      setPickerErrorTo('Неверный формат даты');
    }
  };

  // datetime / time: кастомная модалка с MUI Calendar + наш Clock
  if (dateType === 'datetime' || dateType === 'time') {
    const fromText = from ? from.format(df) : '';
    const toText = to ? to.format(df) : '';

    return (
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!error_from}>
            <div onClick={() => !disabled && setOpenFrom(true)}>
              <TextFieldUI
                fullWidth
                autoComplete="off"
                error={!!error_from}
                disabled={disabled}
                label={labelTextFrom}
                value={fromText}
                InputProps={{ endAdornment: <Adornment /> }}
              />
            </div>
            <DatePickerModal
              isOpen={openFrom}
              handleClose={() => setOpenFrom(false)}
              onChange={(date) => {
                _onChangeFrom(date);
                setOpenFrom(false);
              }}
              dateType={dateType}
              clearable={clearable}
              value={from}
            />
            {(error_from || helperTextFrom) && (
              <FormHelperText>{error_from || helperTextFrom}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!error_to}>
            <div onClick={() => !disabled && setOpenTo(true)}>
              <TextFieldUI
                fullWidth
                autoComplete="off"
                error={!!error_to}
                disabled={disabled}
                label={labelTextTo}
                value={toText}
                InputProps={{ endAdornment: <Adornment /> }}
              />
            </div>
            <DatePickerModal
              isOpen={openTo}
              handleClose={() => setOpenTo(false)}
              onChange={(date) => {
                _onChangeTo(date);
                setOpenTo(false);
              }}
              dateType={dateType}
              clearable={clearable}
              value={to}
            />
            {(error_to || helperTextTo) && (
              <FormHelperText>{error_to || helperTextTo}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>
    );
  }

  // date: KeyboardDatePicker (ручной ввод + стандартный календарь с выбором года)
  const Control = KeyboardDatePicker;

  return (
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <FormControl fullWidth error={!!error_from || !!pickerErrorFrom}>
          <Control
            key={clearKeyFrom}
            clearable={clearable}
            variant="dialog"
            onChange={_onChangeFrom}
            value={from}
            tabIndex={-1}
            error={!!error_from || !!pickerErrorFrom}
            autoComplete="off"
            cancelLabel="Отмена"
            okLabel="Готово"
            clearLabel="Сбросить"
            ampm={false}
            format={df}
            label={labelTextFrom}
            disabled={disabled}
            invalidDateMessage=""
            keyboardIcon={<EventIcon fontSize="small" />}
            KeyboardButtonProps={{ style: { padding: 2 } }}
            InputAdornmentProps={{ style: { marginLeft: 0 } }}
          />
          {(error_from || pickerErrorFrom || helperTextFrom) && (
            <FormHelperText>
              {error_from || pickerErrorFrom || helperTextFrom}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth error={!!error_to || !!pickerErrorTo}>
          <Control
            key={clearKeyTo}
            clearable={clearable}
            variant="dialog"
            value={to}
            onChange={_onChangeTo}
            tabIndex={-1}
            error={!!error_to || !!pickerErrorTo}
            autoComplete="off"
            cancelLabel="Отмена"
            okLabel="Готово"
            clearLabel="Сбросить"
            ampm={false}
            format={df}
            label={labelTextTo}
            disabled={disabled}
            invalidDateMessage=""
            keyboardIcon={<EventIcon fontSize="small" />}
            KeyboardButtonProps={{ style: { padding: 2 } }}
            InputAdornmentProps={{ style: { marginLeft: 0 } }}
          />
          {(error_to || pickerErrorTo || helperTextTo) && (
            <FormHelperText>
              {error_to || pickerErrorTo || helperTextTo}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>
    </Grid>
  );
};

const Adornment = () => (
  <InputAdornment position="end">
    <IconButton
      aria-label="calendar"
      size="small"
      tabIndex={-1}
      style={{ padding: 4 }}>
      <EventIcon fontSize="small" />
    </IconButton>
  </InputAdornment>
);
